/**
 * TransitionChangeEditor Component
 * 
 * Modal/form for editing a single transition_change record.
 * Supports all change types: set, calculate, clear, update, user_input, copy_from_improvement
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Divider,
  Alert
} from 'antd';
import { TransitionChange, StateActions, InputFieldDefinition } from '../../types/globalProperties';
import { InputFieldsEditor } from './InputFieldsEditor';
import {
  getForwardTransitions,
  getBackwardTransitions,
  StandardTransitionInfo
} from '../../types/standardStates';
import { FormInstance } from 'antd';

const { TextArea } = Input;
const { Option } = Select;

// Wrapper component to integrate InputFieldsEditor with Ant Design Form
interface InputFieldsWrapperProps {
  form: FormInstance;
  initialFields: InputFieldDefinition[];
  propertyKey: string;
}

const InputFieldsWrapper: React.FC<InputFieldsWrapperProps> = ({ form, initialFields, propertyKey }) => {
  const [fields, setFields] = useState<InputFieldDefinition[]>(initialFields);

  useEffect(() => {
    setFields(initialFields);
    form.setFieldValue('input_fields', initialFields);
  }, [initialFields, form]);

  const handleChange = (newFields: InputFieldDefinition[]) => {
    setFields(newFields);
    form.setFieldValue('input_fields', newFields);
  };

  return <InputFieldsEditor inputFields={fields} onChange={handleChange} propertyKey={propertyKey} />;
};

interface TransitionChangeEditorProps {
  visible: boolean;
  transitionChange?: TransitionChange;
  stateActions?: StateActions;
  propertyKey: string;
  onSave: (change: TransitionChange) => void;
  onCancel: () => void;
}

const CHANGE_TYPES: Array<{ value: TransitionChange['change_type']; label: string }> = [
  { value: 'set', label: 'Set' },
  { value: 'calculate', label: 'Calculate' },
  { value: 'clear', label: 'Clear' },
  { value: 'update', label: 'Update' },
  { value: 'user_input', label: 'User Input' },
  { value: 'copy_from_improvement', label: 'Copy From Improvement' }
];

export const TransitionChangeEditor: React.FC<TransitionChangeEditorProps> = ({
  visible,
  transitionChange,
  stateActions,
  propertyKey,
  onSave,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [changeType, setChangeType] = useState<TransitionChange['change_type']>('user_input');

  // Get all available transitions
  const getAllAvailableTransitions = (): StandardTransitionInfo[] => {
    const operational = getForwardTransitions('operational');
    const improvement = getForwardTransitions('improvement');
    const operationalBackward = getBackwardTransitions('operational');
    const improvementBackward = getBackwardTransitions('improvement');
    return [...operational, ...improvement, ...operationalBackward, ...improvementBackward];
  };

  const availableTransitions = getAllAvailableTransitions();

  // Generate transition options from state_actions
  const getStateActionOptions = (): string[] => {
    const options: string[] = [];
    if (stateActions?.operational) {
      Object.keys(stateActions.operational).forEach((state) => {
        options.push(`operational.${state}`);
      });
    }
    if (stateActions?.improvement) {
      Object.keys(stateActions.improvement).forEach((state) => {
        options.push(`improvement.${state}`);
      });
    }
    return options;
  };

  const stateActionOptions = getStateActionOptions();

  // Format transition string from StandardTransitionInfo
  const formatTransitionString = (transition: StandardTransitionInfo): string => {
    return `${transition.fromState} → ${transition.toState}`;
  };

  useEffect(() => {
    if (visible && transitionChange) {
      form.setFieldsValue({
        transition: transitionChange.transition,
        state_action: transitionChange.state_action,
        change_type: transitionChange.change_type,
        value: transitionChange.value,
        transformation_logic: transitionChange.transformation_logic
          ? (typeof transitionChange.transformation_logic === 'string'
              ? transitionChange.transformation_logic
              : JSON.stringify(transitionChange.transformation_logic, null, 2))
          : undefined,
        update_logic: transitionChange.update_logic
          ? (typeof transitionChange.update_logic === 'string'
              ? transitionChange.update_logic
              : JSON.stringify(transitionChange.update_logic, null, 2))
          : undefined,
        source_property: transitionChange.source_property,
        input_fields: transitionChange.input_fields || [],
        resource_type: transitionChange.resource_requirements?.resource_type,
        required_skills: transitionChange.resource_requirements?.required_skills?.join(', '),
        resource_description: transitionChange.resource_requirements?.description,
        when_condition: transitionChange.conditions?.when,
        required_if: transitionChange.conditions?.required_if,
        dependencies: transitionChange.dependencies?.join(', ')
      });
      setChangeType(transitionChange.change_type);
    } else if (visible && !transitionChange) {
      // New transition change
      form.resetFields();
      form.setFieldsValue({
        change_type: 'user_input',
        input_fields: []
      });
      setChangeType('user_input');
    }
  }, [visible, transitionChange, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      const change: TransitionChange = {
        change_type: values.change_type,
        transition: values.transition,
        state_action: values.state_action,
        property_key: propertyKey
      };

      // Add fields based on change type
      if (values.change_type === 'set' || values.change_type === 'update') {
        if (values.value !== undefined) {
          change.value = values.value;
        }
      }

      if (values.change_type === 'calculate') {
        if (values.transformation_logic) {
          try {
            change.transformation_logic = JSON.parse(values.transformation_logic);
          } catch {
            change.transformation_logic = values.transformation_logic;
          }
        }
      }

      if (values.change_type === 'update') {
        if (values.update_logic) {
          try {
            change.update_logic = JSON.parse(values.update_logic);
          } catch {
            change.update_logic = values.update_logic;
          }
        }
      }

      if (values.change_type === 'copy_from_improvement') {
        if (values.source_property) {
          change.source_property = values.source_property;
        }
      }

      if (values.change_type === 'user_input') {
        // input_fields will be set separately via the InputFieldsEditor
        const inputFields = form.getFieldValue('input_fields');
        if (inputFields && Array.isArray(inputFields) && inputFields.length > 0) {
          change.input_fields = inputFields;
        }
      }

      // Resource requirements
      if (values.resource_type || values.required_skills || values.resource_description) {
        change.resource_requirements = {
          resource_type: values.resource_type,
          required_skills: values.required_skills
            ? values.required_skills.split(',').map((s: string) => s.trim()).filter(Boolean)
            : [],
          description: values.resource_description
        };
      }

      // Conditions
      if (values.when_condition || values.required_if) {
        change.conditions = {
          when: values.when_condition,
          required_if: values.required_if
        };
      }

      // Dependencies
      if (values.dependencies) {
        change.dependencies = values.dependencies
          .split(',')
          .map((d: string) => d.trim())
          .filter(Boolean);
      }

      onSave(change);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={transitionChange ? 'Edit Transition Change' : 'Add Transition Change'}
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      width={900}
      okText="Save"
      cancelText="Cancel"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="transition"
          label="Transition"
          rules={[{ required: true, message: 'Transition is required' }]}
          tooltip="The transition this change applies to (from_state → to_state)"
        >
          <Select
            placeholder="Select transition"
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.label === 'string' ? option.label : String(option?.label ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {availableTransitions.map((trans) => {
              const transString = formatTransitionString(trans);
              return (
                <Option key={transString} value={transString} label={transString}>
                  {trans.name} ({transString})
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        <Form.Item
          name="state_action"
          label="State Action"
          tooltip="Optional: Link this transition change to a specific state action (e.g., operational.CREATED)"
        >
          <Select placeholder="Select state action (optional)" allowClear>
            {stateActionOptions.map((action) => (
              <Option key={action} value={action}>
                {action}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="change_type"
          label="Change Type"
          rules={[{ required: true, message: 'Change type is required' }]}
        >
          <Select
            onChange={(value) => setChangeType(value)}
          >
            {CHANGE_TYPES.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Divider />

        {/* Conditional fields based on change type */}
        {changeType === 'user_input' && (
          <Form.Item
            name="input_fields"
            label="Input Fields"
            tooltip="Define the input fields that will be shown to users when executing this transition"
          >
            <InputFieldsWrapper
              form={form}
              initialFields={transitionChange?.input_fields || []}
              propertyKey={propertyKey}
            />
          </Form.Item>
        )}

        {changeType === 'set' && (
          <Form.Item
            name="value"
            label="Value"
            tooltip="The value to set for this property"
          >
            <Input placeholder="Enter value" />
          </Form.Item>
        )}

        {changeType === 'calculate' && (
          <Form.Item
            name="transformation_logic"
            label="Transformation Logic"
            tooltip="Logic for calculating the value (JSON object or expression)"
          >
            <TextArea rows={6} placeholder='{"type": "add", "source_properties": {...}}' />
          </Form.Item>
        )}

        {changeType === 'update' && (
          <>
            <Form.Item
              name="value"
              label="Value"
              tooltip="The value to set"
            >
              <Input placeholder="Enter value" />
            </Form.Item>
            <Form.Item
              name="update_logic"
              label="Update Logic"
              tooltip="Logic for updating the value"
            >
              <TextArea rows={4} placeholder="Update logic (JSON or expression)" />
            </Form.Item>
          </>
        )}

        {changeType === 'copy_from_improvement' && (
          <Form.Item
            name="source_property"
            label="Source Property"
            rules={[{ required: true, message: 'Source property is required' }]}
            tooltip="The property key to copy from"
          >
            <Input placeholder="Enter source property key" />
          </Form.Item>
        )}

        {changeType === 'clear' && (
          <Alert
            message="Clear Change Type"
            description="This transition change will clear/reset the property value. No additional configuration needed."
            type="info"
            showIcon
          />
        )}

        <Divider />

        {/* Resource Requirements */}
        <Typography.Title level={5}>Resource Requirements</Typography.Title>
        <Form.Item
          name="resource_type"
          label="Resource Type"
        >
          <Select placeholder="Select resource type" allowClear>
            <Option value="PROCESS_RESOURCE">Process Resource</Option>
            <Option value="USER">User</Option>
            <Option value="SYSTEM">System</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="required_skills"
          label="Required Skills"
          tooltip="Comma-separated list of skill IDs"
        >
          <Input placeholder="skill_id_1, skill_id_2, ..." />
        </Form.Item>

        <Form.Item
          name="resource_description"
          label="Description"
          tooltip="Why these skills/resources are needed"
        >
          <TextArea rows={2} placeholder="Description of resource requirements" />
        </Form.Item>

        <Divider />

        {/* Conditions */}
        <Typography.Title level={5}>Conditions</Typography.Title>
        <Form.Item
          name="when_condition"
          label="When Condition"
          tooltip='Condition expression (e.g., "is_improvement = TRUE")'
        >
          <Input placeholder="Condition expression" />
        </Form.Item>

        <Form.Item
          name="required_if"
          label="Required If"
          tooltip="Additional condition for when this change is required"
        >
          <Input placeholder="Required if condition" />
        </Form.Item>

        <Divider />

        {/* Dependencies */}
        <Form.Item
          name="dependencies"
          label="Dependencies"
          tooltip="Comma-separated list of property keys or artifact IDs this change depends on"
        >
          <Input placeholder="property_key_1, property_key_2, ..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

